/**
 * El plano del piso, en el resumen del inmueble.
 *
 * NO ES UN DATO NUEVO: es el papel de tipo «Planos» de la pestaña Documentos. Se lee de la misma
 * consulta (`propertyDeeds`), así que subir o borrar un plano allí lo cambia aquí sin más. Sin
 * ningún plano con fichero, no se pinta nada: una tarjeta vacía que diga «sin plano» no aporta.
 *
 * Una imagen se enseña tal cual, con su URL firmada, igual que las fotos: mirar el resumen no es
 * descargar un documento y no debe dejar rastro en la auditoría cada vez. Un PDF se enseña
 * también como imagen: su PRIMERA PÁGINA, que la API rasteriza y guarda
 * (`GET /documents/:id/preview`), sin cargar un lector de PDF en la página. Pulsar el plano lo
 * abre en el visor, en grande (el PDF entero, con todas sus páginas). Si la vista previa falla,
 * queda la fila que lo abre igual.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, queryKeys, type PropertyDeedDto } from '../../api/client';
import { Card } from '../../components/Card';
import { Icon } from '../../components/Icon';
import { ConfirmDialog } from '../../components/overlay/Drawer';
import { toast } from '../../components/feedback/Toaster';
import { useDocumentViewer } from '../../components/overlay/DocumentViewer';
import styles from './PropertyFloorPlan.module.css';

const esImagen = (file: NonNullable<PropertyDeedDto['file']>) =>
  file.mimeType ? file.mimeType.startsWith('image/') : /\.(jpe?g|png|webp|avif|gif)$/i.test(file.name);

const esPdf = (file: NonNullable<PropertyDeedDto['file']>) =>
  file.mimeType === 'application/pdf' || /\.pdf$/i.test(file.name);

/** Lo que acepta el servidor para un documento del inmueble (igual que en Documentos). */
const ACEPTA = 'application/pdf,image/jpeg,image/png,image/webp';

export function PropertyFloorPlan({ propertyId, canManage = false }: { propertyId: string; canManage?: boolean }) {
  const queryClient = useQueryClient();
  const deeds = useQuery({
    queryKey: queryKeys.propertyDeeds(propertyId),
    queryFn: () => api.propertyDeeds(propertyId),
  });

  function refrescar() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.propertyDeeds(propertyId) });
    // La ficha cuenta los documentos en su pestaña: también se refresca.
    void queryClient.invalidateQueries({ queryKey: queryKeys.property(propertyId) });
  }

  /*
   * Sustituir = subir el nuevo y quitar el anterior. Al revés no: si la subida falla, el plano
   * viejo sigue ahí. Y se quita, no se deja al lado, porque esta tarjeta enseña TODOS los planos
   * y quedarían el viejo y el nuevo uno debajo del otro.
   */
  const sustituir = useMutation({
    mutationFn: async ({ anterior, file }: { anterior: PropertyDeedDto; file: File }) => {
      await api.uploadDeed(propertyId, file, { kind: 'planos' });
      await api.deleteDeed(anterior.id);
    },
    onSuccess: () => toast.success('Plano sustituido.'),
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido sustituir el plano.'),
    onSettled: refrescar,
  });

  const quitar = useMutation({
    mutationFn: (deed: PropertyDeedDto) => api.deleteDeed(deed.id),
    onSuccess: () => toast.success('Plano quitado. El fichero se conserva en el histórico.'),
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido quitar el plano.'),
    onSettled: refrescar,
  });

  const planos = (deeds.data?.data ?? []).filter((deed) => deed.kind === 'planos' && deed.file);
  if (planos.length === 0) return null;
  /*
   * Con algún plano en imagen, la tarjeta se estira para llegar justo hasta el final de la
   * tarjeta de fotos, y la imagen se ajusta dentro (sin recortarse). Con solo PDFs, que son
   * una fila, se queda a su altura.
   */
  const conImagen = planos.some((deed) => esImagen(deed.file!) || esPdf(deed.file!));

  return (
    <Card variant="sm" className={conImagen ? styles.cardFlexible : undefined}>
      {/* Con un solo plano, el lápiz y la papelera van en la cabecera, a la altura del título. */}
      <div className={styles.cabecera}>
        <div className={styles.title}>{planos.length === 1 ? 'Plano' : 'Planos'}</div>
        {canManage && planos.length === 1 && (
          <AccionesPlano
            deed={planos[0]!}
            ocupado={sustituir.isPending || quitar.isPending}
            onSustituir={(file) => sustituir.mutate({ anterior: planos[0]!, file })}
            onQuitar={() => quitar.mutateAsync(planos[0]!)}
          />
        )}
      </div>
      <div className={styles.list}>
        {planos.map((deed) => (
          <div key={deed.id} className={styles.plano}>
            <Plano deed={deed} />
            {/* Con varios, cada uno lleva los suyos sobre la imagen: si no, no se sabría de cuál son. */}
            {canManage && planos.length > 1 && (
              <AccionesPlano
                deed={deed}
                sobreImagen
                ocupado={sustituir.isPending || quitar.isPending}
                onSustituir={(file) => sustituir.mutate({ anterior: deed, file })}
                onQuitar={() => quitar.mutateAsync(deed)}
              />
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * El lápiz (sustituir el plano por otro fichero) y la papelera (quitarlo). Van FUERA del botón
 * que abre el visor: un botón dentro de otro no es HTML válido y el clic en la papelera abriría
 * además el plano.
 */
function AccionesPlano({
  deed,
  ocupado,
  onSustituir,
  onQuitar,
  sobreImagen = false,
}: {
  deed: PropertyDeedDto;
  ocupado: boolean;
  onSustituir: (file: File) => void;
  onQuitar: () => Promise<unknown>;
  /** Encima de la imagen (con varios planos) en vez de en la cabecera. */
  sobreImagen?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [confirmando, setConfirmando] = useState(false);

  return (
    <div className={sobreImagen ? `${styles.acciones} ${styles.accionesSobre}` : styles.acciones}>
      <button
        type="button"
        className={styles.accion}
        onClick={() => inputRef.current?.click()}
        disabled={ocupado}
        aria-label={`Sustituir el plano «${deed.name}»`}
        title="Sustituir por otro fichero"
      >
        <Icon name="edit" size={14} strokeWidth={2} />
      </button>
      <button
        type="button"
        className={`${styles.accion} ${styles.accionPeligro}`}
        onClick={() => setConfirmando(true)}
        disabled={ocupado}
        aria-label={`Quitar el plano «${deed.name}»`}
        title="Quitar el plano"
      >
        <Icon name="trash" size={14} strokeWidth={2} />
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACEPTA}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onSustituir(file);
          // Para que elegir el MISMO fichero otra vez vuelva a disparar el cambio.
          event.target.value = '';
        }}
      />
      {confirmando && (
        <ConfirmDialog
          title="Quitar el plano"
          body={`«${deed.name}» deja de verse en la ficha y en Documentos. El fichero no se borra del histórico.`}
          confirmLabel="Quitar"
          tone="danger"
          onCancel={() => setConfirmando(false)}
          onConfirm={async () => {
            await onQuitar();
            setConfirmando(false);
          }}
        />
      )}
    </div>
  );
}

function Plano({ deed }: { deed: PropertyDeedDto }) {
  const visor = useDocumentViewer();
  // La URL firmada caduca: si la imagen ya no carga, queda la fila que la abre en el visor.
  const [fallo, setFallo] = useState(false);
  const file = deed.file!;
  const abrir = () => visor.open({ id: file.documentId, name: file.name, meta: deed.kindLabel });

  if (esPdf(file)) return <PlanoPdf deed={deed} onOpen={abrir} />;

  if (esImagen(file) && !fallo) {
    return (
      <button type="button" className={styles.imageButton} onClick={abrir} aria-label={`Ver el plano «${deed.name}» en grande`}>
        <img className={styles.image} src={file.url} alt={`Plano: ${deed.name}`} loading="lazy" onError={() => setFallo(true)} />
        <span className={styles.zoom} aria-hidden="true">
          <Icon name="search" size={14} strokeWidth={2.2} />
        </span>
      </button>
    );
  }

  return <FilaPlano deed={deed} onOpen={abrir} />;
}

/** Un plano en PDF: su primera página como imagen; pulsarla abre el PDF entero en el visor. */
function PlanoPdf({ deed, onOpen }: { deed: PropertyDeedDto; onOpen: () => void }) {
  const file = deed.file!;
  const vista = useQuery({
    queryKey: ['document-preview', file.documentId],
    queryFn: async () => (await api.documentPreview(file.documentId)).blob,
    // El documento no cambia: la imagen tampoco. Se pide una vez por sesión.
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: 1,
  });
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!vista.data) return undefined;
    const enlace = URL.createObjectURL(vista.data);
    setUrl(enlace);
    return () => URL.revokeObjectURL(enlace);
  }, [vista.data]);

  if (vista.isError) return <FilaPlano deed={deed} onOpen={onOpen} />;

  return (
    <button type="button" className={styles.imageButton} onClick={onOpen} aria-label={`Ver el plano «${deed.name}» en grande`}>
      {url ? (
        <img className={styles.image} src={url} alt={`Plano: ${deed.name}`} />
      ) : (
        <span className={styles.cargando} role="status">
          <span className={styles.cargandoRueda} aria-hidden="true" />
          Preparando el plano…
        </span>
      )}
      <span className={styles.etiquetaPdf} aria-hidden="true">
        PDF
      </span>
      <span className={styles.zoom} aria-hidden="true">
        <Icon name="search" size={14} strokeWidth={2.2} />
      </span>
    </button>
  );
}

/** La fila que abre el plano en el visor, cuando no se puede enseñar como imagen. */
function FilaPlano({ deed, onOpen }: { deed: PropertyDeedDto; onOpen: () => void }) {
  return (
    <button type="button" className={styles.fileRow} onClick={onOpen}>
      <span className={styles.fileIcon} aria-hidden="true">
        <Icon name="file-lines" size={16} />
      </span>
      <span className={styles.fileName}>{deed.name}</span>
      <span className={styles.fileAction}>
        <Icon name="eye" size={15} strokeWidth={2} />
        Ver plano
      </span>
    </button>
  );
}
